import type { TFunction } from 'i18next';
import { z } from 'zod';

/**
 * Payout address kinds crypto-api settles to, in display order. Mirrors `CryptoOnchainPayoutAddressKind`
 * on the server (src/lib/schemas/cryptoOnchain.ts); the CRM cannot import server code.
 */
export const CRYPTO_ONCHAIN_PAYOUT_ADDRESS_KINDS = ['evm', 'solana', 'tron', 'btc', 'ltc', 'doge'] as const;
export type CryptoOnchainPayoutAddressKind = (typeof CRYPTO_ONCHAIN_PAYOUT_ADDRESS_KINDS)[number];

/**
 * Kinds merchants can use today; the others show as "coming soon". Mirrors
 * `CRYPTO_ONCHAIN_LIVE_PAYOUT_ADDRESS_KINDS` (src/lib/schemas/cryptoOnchain.ts), which refuses the rest.
 * These fields take mainnet addresses only (crypto-api refuses a testnet one there): a sandbox
 * store's Bitcoin and Litecoin testnet wallets go in the test network wallets card.
 */
export const CRYPTO_ONCHAIN_LIVE_PAYOUT_ADDRESS_KINDS: readonly CryptoOnchainPayoutAddressKind[] = ['evm', 'solana'];

export function isCryptoOnchainPayoutKindLive(kind: CryptoOnchainPayoutAddressKind): boolean {
  return CRYPTO_ONCHAIN_LIVE_PAYOUT_ADDRESS_KINDS.includes(kind);
}

/** One string per kind; '' means "no address for this chain" and clears a stored one on save. */
export type CryptoOnchainFormValues = Record<CryptoOnchainPayoutAddressKind, string>;

/**
 * Format checks only, so a typo is caught before the round trip. crypto-api validates for real
 * (checksums, bech32) and its refusal comes back as the `INVALID_PAYOUT_ADDRESS` business code.
 */
export const CRYPTO_ONCHAIN_ADDRESS_PATTERNS: Record<CryptoOnchainPayoutAddressKind, RegExp> = {
  evm: /^0x[0-9a-fA-F]{40}$/,
  solana: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
  tron: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
  btc: /^(bc1[ac-hj-np-z02-9]{11,71}|[13][1-9A-HJ-NP-Za-km-z]{25,34})$/,
  ltc: /^(ltc1[ac-hj-np-z02-9]{11,71}|[LM][1-9A-HJ-NP-Za-km-z]{26,33})$/,
  doge: /^D[1-9A-HJ-NP-Za-km-z]{33}$/,
};

export function isValidCryptoOnchainAddress(kind: CryptoOnchainPayoutAddressKind, value: string): boolean {
  return CRYPTO_ONCHAIN_ADDRESS_PATTERNS[kind].test(value);
}

export const EMPTY_CRYPTO_ONCHAIN_FORM_VALUES: CryptoOnchainFormValues = {
  evm: '',
  solana: '',
  tron: '',
  btc: '',
  ltc: '',
  doge: '',
};

/** Stored addresses (the status DTO) to form values: a missing or null address is an empty field. */
export function toCryptoOnchainFormValues(
  addresses: Partial<Record<CryptoOnchainPayoutAddressKind, string | null>>,
): CryptoOnchainFormValues {
  return {
    evm: addresses.evm ?? '',
    solana: addresses.solana ?? '',
    tron: addresses.tron ?? '',
    btc: addresses.btc ?? '',
    ltc: addresses.ltc ?? '',
    doge: addresses.doge ?? '',
  };
}

/**
 * The mutation input: every live kind is sent, trimmed, '' clearing a stored address (CONTRACT §A).
 * A coming-soon kind is left out, so an address stored before it was closed is kept, not re-sent.
 */
export function toCryptoOnchainPayoutAddressesInput(
  values: CryptoOnchainFormValues,
): Partial<CryptoOnchainFormValues> {
  return Object.fromEntries(
    CRYPTO_ONCHAIN_PAYOUT_ADDRESS_KINDS.filter(isCryptoOnchainPayoutKindLive).map((kind) => [kind, values[kind].trim()]),
  );
}

export function hasAnyCryptoOnchainAddress(values: CryptoOnchainFormValues): boolean {
  return CRYPTO_ONCHAIN_LIVE_PAYOUT_ADDRESS_KINDS.some((kind) => values[kind].trim() !== '');
}

type AddressField = z.ZodEffects<z.ZodString, string, string>;

/** Concrete zod type (not the `ZodType` interface): `zodResolver` needs the `_def.typeName` of a zod 3 schema. */
export type CryptoOnchainFormSchema = z.ZodEffects<
  z.ZodObject<Record<CryptoOnchainPayoutAddressKind, AddressField>>,
  CryptoOnchainFormValues,
  CryptoOnchainFormValues
>;

/**
 * The form schema, with translated messages. Every field is optional on its own; the form as a whole
 * needs at least one address (reported on the EVM field, the one we recommend filling first).
 */
export function buildCryptoOnchainFormSchema(t: TFunction<'integrations'>): CryptoOnchainFormSchema {
  const messages: Record<CryptoOnchainPayoutAddressKind, string> = {
    evm: t('cryptoOnchain.validation.evm'),
    solana: t('cryptoOnchain.validation.solana'),
    tron: t('cryptoOnchain.validation.tron'),
    btc: t('cryptoOnchain.validation.btc'),
    ltc: t('cryptoOnchain.validation.ltc'),
    doge: t('cryptoOnchain.validation.doge'),
  };
  const address = (kind: CryptoOnchainPayoutAddressKind) =>
    z
      .string()
      .trim()
      .refine((value) => value === '' || isValidCryptoOnchainAddress(kind, value), {
        message: messages[kind],
      });

  return z
    .object({
      evm: address('evm'),
      solana: address('solana'),
      tron: address('tron'),
      btc: address('btc'),
      ltc: address('ltc'),
      doge: address('doge'),
    })
    .superRefine((values, ctx) => {
      if (hasAnyCryptoOnchainAddress(values)) return;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['evm'],
        message: t('cryptoOnchain.validation.atLeastOne'),
      });
    });
}
